import React, { useContext, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { CalloutContext, useOkapiKy, useStripes } from '@folio/stripes/core';
import EmailTemplateForm from './EmailTemplateForm';

const CONFIG_SUBJECT = 'DEFAULT_EMAIL_SUBJECT';
const CONFIG_BODY    = 'DEFAULT_EMAIL_BODY';

const parseLocalized = (value) => {
  if (!value) return [];
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    // legacy plain string — wrap as English
    return value.trim() ? [{ locale: 'en', value }] : [];
  }
};

function EmailTemplateManager(props) {
  const okapiKy = useOkapiKy();
  const stripes = useStripes();
  const canManage = stripes.hasPerm('ui-reports.configs.manage');
  const callout = useContext(CalloutContext);

  const [initialValues, setInitialValues] = useState({ emailSubject: [], emailBody: [] });
  const [configIds,     setConfigIds]     = useState({});
  const [isLoading,     setIsLoading]     = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await okapiKy
          .get(`reportsConfigs?query=(configCode=="${CONFIG_SUBJECT}" or configCode=="${CONFIG_BODY}")&limit=2`)
          .json();
        const configs = res.reportsConfigs || [];
        const ids = {};
        const values = { emailSubject: [], emailBody: [] };

        configs.forEach(c => {
          ids[c.configCode] = c.id;
          if (c.configCode === CONFIG_SUBJECT) values.emailSubject = parseLocalized(c.value);
          if (c.configCode === CONFIG_BODY)    values.emailBody    = parseLocalized(c.value);
        });

        setConfigIds(ids);
        setInitialValues(values);
      } catch {
        callout.sendCallout({
          type: 'error',
          message: <FormattedMessage id="ui-reports.settings.emailTemplate.loadError" defaultMessage="Error loading email templates" />,
        });
      } finally {
        setIsLoading(false);
      }
    };
    fetch();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const saveConfig = async (code, arrayValue, existingId) => {
    const payload = {
      configCode: code,
      configName: code === CONFIG_SUBJECT ? 'Default Email Subject Template' : 'Default Email Body Template',
      value: JSON.stringify(arrayValue),
      enabled: true,
    };
    if (existingId) {
      await okapiKy.put(`reportsConfigs/${existingId}`, { json: { id: existingId, ...payload } });
    } else {
      const created = await okapiKy.post('reportsConfigs', { json: payload }).json();
      setConfigIds(prev => ({ ...prev, [code]: created.id }));
    }
  };

  const handleSubmit = async (values) => {
    setIsLoading(true);
    try {
      await Promise.all([
        saveConfig(CONFIG_SUBJECT, values.emailSubject || [], configIds[CONFIG_SUBJECT]),
        saveConfig(CONFIG_BODY,    values.emailBody    || [], configIds[CONFIG_BODY]),
      ]);
      callout.sendCallout({
        type: 'success',
        message: <FormattedMessage id="ui-reports.settings.emailTemplate.saved" defaultMessage="Email templates saved successfully" />,
      });
    } catch {
      callout.sendCallout({
        type: 'error',
        message: <FormattedMessage id="ui-reports.settings.emailTemplate.saveError" defaultMessage="Error saving email templates" />,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <EmailTemplateForm
      {...props}
      onSubmit={handleSubmit}
      canManage={canManage}
      initialValues={initialValues}
      isLoading={isLoading}
    />
  );
}

export default EmailTemplateManager;
